import React from 'react';

import { Period } from 'common/modules/domain/plan';
import {
  ServiceInformation,
  LinksInformation
} from 'common/modules/domain/plan';
import {
  Button,
  Title,
  Notes,
  Description,
  LinkText,
  Spinner
} from 'common/components';
import { Glossary, PlanDetailGL } from 'common/utils/glossary';
import { appBaseUrl } from 'common/utils/urls';

import { Service } from './Service';

interface Props {
  /** 表示するサービス */
  services: ServiceInformation[];
  /** 税込価格 */
  taxIncludedPrice: number;
  /** 通貨 */
  currency: string;
  /** 有効期間(31日 or 365日 or なし) */
  validityPeriod: number | null;
  /** 利用制限のある売り切りプランの有効期限（日単位。未設定の場合は0） */
  perpetualValidityPeriod: number | null;
  /** 利用上限 */
  usageLimit: number | null;
  /** 静的コンテンツへのリンク */
  staticLinks: LinksInformation[];
  /** 利用言語 */
  language: string;
  /** 処理中か否か */
  isProcessingPurchase: boolean;
  /** グロッサリ */
  glossary: Glossary;
  onInit: () => void;
  onClick: () => void;
}

/**
 * プラン詳細画面の下段リスト部分のPresentation Component
 */
export const Services: React.FC<Props> = ({
  services,
  taxIncludedPrice,
  currency,
  validityPeriod,
  perpetualValidityPeriod,
  usageLimit,
  staticLinks,
  language,
  isProcessingPurchase,
  glossary,
  onInit,
  onClick
}) => {
  const [isShowLinks, setIsShowLinks] = React.useState<boolean>(false);
  const buttonImage = isShowLinks
    ? 'list_area_close.svg'
    : 'list_area_open.svg';
  const buttonStyle: React.CSSProperties = {
    backgroundImage: `url("${appBaseUrl}/st/images/common/${buttonImage}")`
  };
  // 価格をローカライズ化
  const localize_price =
    taxIncludedPrice !== null
      ? taxIncludedPrice.toLocaleString(language, {
          style: 'currency',
          currency: currency
        })
      : '';
  // 期間が未定義の場合は売り切りと判断し、グロッサリーを利用しない
  const priceLabel = validityPeriod
    ? glossary
        .glx(PlanDetailGL.SubscriptionPrice)
        .replace('{0}', localize_price)
        .replace('{1}', validityPeriod.toString())
    : localize_price;
  // 利用上限と有効期限
  const usageLimitLabel = usageLimit
    ? glossary
        .glx(PlanDetailGL.UsageLimit)
        .replace('{0}', usageLimit.toString())
    : '';
  const perpetualValidityPeriodLabel = perpetualValidityPeriod
    ? glossary
        .glx(PlanDetailGL.PerpetualValidityPeriod)
        .replace('{0}', perpetualValidityPeriod.toString())
    : '';

  /** 初期化イベント */
  React.useEffect(() => {
    onInit();
  }, []);

  return (
    <div className="list" data-test="planDetailServices">
      {services.map(
        (service, index) =>
          service && (
            <Service key={index} service={service} glossary={glossary} />
          )
      )}
      {staticLinks && staticLinks.length ? (
        <div className="staticContent" data-test="planDetailStaticContentLinks">
          <Button
            type="showLinkButton"
            style={buttonStyle}
            onClick={() => {
              setIsShowLinks(!isShowLinks);
            }}
            data-test="planDetailShowLinksButton"
          >
            {glossary.glx(PlanDetailGL.SeeRelatedLinks)}
          </Button>
          <Description
            type="staticContentLinks"
            data-test="planDetailStaticContentLinkTexts"
            style={{ display: !isShowLinks ? 'none' : 'block' }}
          >
            {staticLinks.map(
              (link, index) =>
                link &&
                link.url && (
                  <LinkText
                    type="staticContentLink"
                    key={index}
                    onClick={() => window.open(`${link.url}`)}
                    data-test={`planDetailLink-${index}`}
                  >
                    {link.title}
                  </LinkText>
                )
            )}
          </Description>
        </div>
      ) : null}
      <Title type="price" data-test="planDetailTaxIncludedPrice">
        {`${priceLabel}`}
      </Title>
      {usageLimitLabel ? (
        <Title type="price" data-test="planDetailUsageLimit">
          {usageLimitLabel}
        </Title>
      ) : null}
      {perpetualValidityPeriodLabel ? (
        <Title type="price" data-test="planDetailPerpetualValidityPeriod">
          {perpetualValidityPeriodLabel}
        </Title>
      ) : null}
      <Button
        type="nextButton"
        onClick={onClick}
        data-test="planDetailPurchase"
      >
        {isProcessingPurchase ? (
          <Spinner type="button" width={22} height={22} />
        ) : (
          glossary.glx(PlanDetailGL.Purchase)
        )}
      </Button>
      <Notes
        notes={[
          glossary.glx(PlanDetailGL.Note1),
          glossary.glx(PlanDetailGL.Note2)
        ]}
        data-test="planDetailNotes"
      />
    </div>
  );
};
